import React, { useContext, useState } from 'react';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import { GlobalContext } from '../../context/GlobalState';
import S from './style';
import Title from '../Title';
import CardItem from '../CardItem';
import AddButton from '../AddButton';
import { createCard, updateCard, deleteCard } from '../../services/cards';
import { increaseVote, decreaseVote } from '../../services/votes';
import { logEvent, TrackingEvents } from '../../tracking';

function ColumnBoard({ column }) {
  const [newCard, setNewCard] = useState();
  const { cards, user, retrospective, currentRetrospectiveId } =
    useContext(GlobalContext);

  const { stage, facilitator, votesLimit } = retrospective;

  const getNextRank = () => {
    const columnCards = cards.filter((card) => card.columnName === column);
    if (columnCards.length === 0) return 10000;
    return columnCards[columnCards.length - 1].rank + 10000;
  };

  const onAddCard = () => {
    setNewCard({ description: '' });
  };

  const onEditCard = (card, description) => {
    if (!card.id) {
      logEvent(TrackingEvents.CREATE_CARD, { stage });
      createCard(currentRetrospectiveId, description, column, getNextRank());
      setNewCard();
    } else {
      logEvent(TrackingEvents.UPDATE_CARD, { stage });
      updateCard(currentRetrospectiveId, card.id, { description });
    }
  };

  const onDeleteCard = (card) => {
    if (!card.id) {
      setNewCard();
    } else {
      logEvent(TrackingEvents.DELETE_CARD, { stage });
      deleteCard(currentRetrospectiveId, card.id);
    }
  };

  const onVoteCard = (card) => {
    logEvent(TrackingEvents.VOTE_CARD);
    increaseVote(currentRetrospectiveId, card.id);
  };

  const onUnvoteCard = (card) => {
    logEvent(TrackingEvents.UNVOTE_CARD);
    decreaseVote(currentRetrospectiveId, card.id);
  };

  const isEditable = () =>
    stage === 'THINK' || (stage === 'GROUP' && user.id === facilitator);

  const userVotes = () =>
    cards.reduce((a, c) => (c.votes.includes(user.id) ? a + 1 : a), 0);

  const isVoteVisible = () => stage === 'VOTE';

  const isVotable = () => !votesLimit || votesLimit - userVotes() > 0;

  const isGroupable = () => stage === 'GROUP';

  return (
    <Droppable droppableId={column} isCombineEnabled={isGroupable()}>
      {(provided, snapshot) => (
        <S.Wrapper isDraggingOver={snapshot.isDraggingOver}>
          <Title size="sm">{column}</Title>

          <S.WrapperScroll ref={provided.innerRef}>
            <S.Scroll>
              {cards
                .filter((card) => card.columnName === column)
                .filter((card) =>
                  stage === 'THINK' ? card.createdBy === user.id : card
                )
                .map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={!isEditable()}
                  >
                    {(providedItemCard, snapshotItemCard) => (
                      <CardItem
                        ref={providedItemCard.innerRef}
                        isDragging={snapshotItemCard.isDragging}
                        isCombining={Boolean(snapshotItemCard.combineTargetFor)}
                        style={{ ...providedItemCard.draggableProps.style }}
                        data={item}
                        onAddCard={onAddCard}
                        onEditCard={(description) =>
                          onEditCard(item, description)
                        }
                        onDeleteCard={() => onDeleteCard(item)}
                        readOnly={!isEditable()}
                        isVoteVisible={isVoteVisible()}
                        isVotable={isVotable()}
                        isDraggable={isEditable()}
                        onVoteCard={() => onVoteCard(item)}
                        onUnvoteCard={() => onUnvoteCard(item)}
                        {...providedItemCard.draggableProps}
                        {...providedItemCard.dragHandleProps}
                      />
                    )}
                  </Draggable>
                ))}
              {newCard && (
                <Draggable
                  key={-1}
                  draggableId="-1"
                  index={
                    cards.filter((card) =>
                      stage === 'THINK' ? card.createdBy === user.id : card
                    ).length
                  }
                  isDragDisabled
                >
                  {(providedItem) => (
                    <CardItem
                      ref={providedItem.innerRef}
                      style={{ ...providedItem.draggableProps.style }}
                      data={newCard}
                      onAddCard={onAddCard}
                      onEditCard={(description) =>
                        onEditCard(newCard, description)
                      }
                      onDeleteCard={() => onDeleteCard(newCard)}
                      isNewCard
                    />
                  )}
                </Draggable>
              )}

              {provided.placeholder}
            </S.Scroll>
          </S.WrapperScroll>

          {isEditable() && (
            <AddButton onClick={onAddCard}>Add an idea</AddButton>
          )}
        </S.Wrapper>
      )}
    </Droppable>
  );
}

export default ColumnBoard;
